<template>
  <div
    class="lighting-advisor-question-field"
    :data-testid="`lighting-advisor-question-${question.id}`"
  >
    <div class="lighting-advisor-question-field__label-row">
      <b :id="questionLabelId" class="lighting-advisor-question-field__label">
        {{ question.content }}
      </b>
      <button
        v-if="tooltipKeys.length > 0"
        type="button"
        class="lighting-advisor-question-field__info-btn"
        :aria-label="infoButtonLabel"
        @click="emit('open-tooltip', question)"
      >
        <Icon name="info" size="small" color="#0094ff" />
      </button>
    </div>

    <template v-if="question.id === DIMENSION_QUESTION_ID">
      <div
        class="lighting-advisor-question-field__dimensions"
        role="group"
        :aria-labelledby="questionLabelId"
      >
        <InputText
          :model-value="String(answers.a2s2q2_length ?? '')"
          type="number"
          inputmode="numeric"
          :placeholder="
            $textDictionary['lightingAdvisor.input.length.placeholder'] ??
            'Length (mm)'
          "
          data-testid="lighting-advisor-input-a2s2q2-length"
          @update:model-value="emitAnswer('a2s2q2_length', $event)"
          @focus="emit('update:focused-field', 'length')"
          @blur="emit('update:focused-field', null)"
        />
        <InputText
          :model-value="String(answers.a2s2q2_width ?? '')"
          type="number"
          inputmode="numeric"
          :placeholder="
            $textDictionary['lightingAdvisor.input.width.placeholder'] ??
            'Width (mm)'
          "
          data-testid="lighting-advisor-input-a2s2q2-width"
          @update:model-value="emitAnswer('a2s2q2_width', $event)"
          @focus="emit('update:focused-field', 'width')"
          @blur="emit('update:focused-field', null)"
        />
      </div>
    </template>

    <template v-else-if="question.id === DISTANCE_QUESTION_ID">
      <InputText
        :aria-labelledby="questionLabelId"
        :model-value="String(answers.a2s2q1 ?? '')"
        type="number"
        inputmode="numeric"
        :placeholder="
          $textDictionary['lightingAdvisor.input.distance.placeholder'] ??
          'Distance (mm)'
        "
        data-testid="lighting-advisor-input-a2s2q1"
        @update:model-value="emitAnswer(question.id, $event)"
        @focus="emit('update:focused-field', 'distance')"
        @blur="emit('update:focused-field', null)"
      />
    </template>

    <template v-else-if="isCheckbox">
      <div
        class="lighting-advisor-question-field__option-group"
        :class="optionGroupClass"
        role="group"
        :aria-labelledby="questionLabelId"
        data-testid="lighting-advisor-step-4-functions"
      >
        <div
          v-for="option in options"
          :key="option.value"
          class="lighting-advisor-question-field__option-row"
        >
          <PrimeVueCheckbox
            :input-id="`${question.id}-${option.value}`"
            :model-value="isFunctionSelected(option.value)"
            binary
            :data-testid="`lighting-advisor-input-${question.id}-${option.value}`"
            @update:model-value="toggleFunction(option.value)"
          />
          <label
            class="lighting-advisor-question-field__option-label"
            :for="`${question.id}-${option.value}`"
          >
            {{ option.label }}
          </label>
        </div>
      </div>
    </template>

    <template v-else-if="options.length > 0 && isRadio">
      <div
        class="lighting-advisor-question-field__option-group"
        :class="optionGroupClass"
        role="radiogroup"
        :aria-labelledby="questionLabelId"
      >
        <div
          v-for="option in options"
          :key="option.value"
          class="lighting-advisor-question-field__option-row"
          :data-testid="`lighting-advisor-input-${question.id}-${option.value}`"
        >
          <PrimeVueRadioButton
            :input-id="`${question.id}-${option.value}`"
            :model-value="answers[question.id] ?? null"
            :value="option.value"
            :name="question.id"
            @update:model-value="emitAnswer(question.id, option.value)"
          />
          <label
            class="lighting-advisor-question-field__option-label"
            :for="`${question.id}-${option.value}`"
          >
            {{ option.label }}
          </label>
        </div>
      </div>

      <div
        v-if="nestedQuestion"
        class="lighting-advisor-question-field__nested"
        :data-testid="`lighting-advisor-question-${nestedQuestion.id}`"
      >
        <div class="lighting-advisor-question-field__label-row">
          <b
            :id="nestedQuestionLabelId"
            class="lighting-advisor-question-field__label"
          >
            {{ nestedQuestion.content }}
          </b>
          <button
            v-if="nestedTooltipKeys.length > 0"
            type="button"
            class="lighting-advisor-question-field__info-btn"
            :aria-label="infoButtonLabel"
            @click="emit('open-tooltip', nestedQuestion)"
          >
            <Icon name="info" size="small" color="#0094ff" />
          </button>
        </div>
        <div
          class="lighting-advisor-question-field__option-group"
          :class="nestedOptionGroupClass"
          role="radiogroup"
          :aria-labelledby="nestedQuestionLabelId"
        >
          <div
            v-for="option in nestedOptions"
            :key="option.value"
            class="lighting-advisor-question-field__option-row"
            :data-testid="`lighting-advisor-input-${nestedQuestion.id}-${option.value}`"
          >
            <PrimeVueRadioButton
              :input-id="`${nestedQuestion.id}-${option.value}`"
              :model-value="answers[nestedQuestion.id] ?? null"
              :value="option.value"
              :name="nestedQuestion.id"
              @update:model-value="emitAnswer(nestedQuestion.id, option.value)"
            />
            <label
              class="lighting-advisor-question-field__option-label"
              :for="`${nestedQuestion.id}-${option.value}`"
            >
              {{ option.label }}
            </label>
          </div>
        </div>
        <InfoIcon
          v-for="(warning, index) in nestedWarnings"
          :key="`${nestedQuestion.id}-warning-${index}`"
          class="lighting-advisor-question-field__warning-icon"
          icon="attention"
          tag="span"
          :has-pulse-animation="true"
          :tooltip-text="warning.text"
          :style="'warning'"
        />
      </div>
    </template>

    <template v-else-if="options.length > 0">
      <SelectableOptionItems
        :display-type="choiceDisplayType"
        :grid-columns="
          choiceDisplayType === 'grid'
            ? choiceGridColumns(question.id)
            : undefined
        "
        selection-mode="radio"
        :aria-label="question.content"
      >
        <div class="selectable-option-items__list">
          <SelectableOptionItem
            v-for="option in options"
            :key="option.value"
            selection-mode="radio"
            :content-layout="option.image ? 'stacked' : 'inline'"
            :checked="answers[question.id] === option.value"
            :value="option.value"
            :title="option.label"
            :image="option.image"
            :data-testid="`lighting-advisor-input-${question.id}-${option.value}`"
            @select="emitAnswer(question.id, $event)"
          >
            <template v-if="isFurtherApplicationOption(option.value)" #input>
              <div
                class="form-item lighting-advisor-question-field__further-input"
              >
                <PrimeVueFloatLabel
                  variant="on"
                  :class="{
                    'p-floatlabel-disabled':
                      answers.a2s5q1 !== 'further_application'
                  }"
                >
                  <PrimeVueInputText
                    :id="furtherApplicationInputId"
                    :model-value="String(answers.a2s5q1_further ?? '')"
                    :name="furtherApplicationInputId"
                    :disabled="answers.a2s5q1 !== 'further_application'"
                    data-testid="lighting-advisor-step-5-further-application"
                    @update:model-value="emitAnswer('a2s5q1_further', $event)"
                  />
                  <label class="p-label" :for="furtherApplicationInputId">
                    {{ furtherApplicationLabel }}
                  </label>
                </PrimeVueFloatLabel>
              </div>
            </template>
          </SelectableOptionItem>
        </div>
      </SelectableOptionItems>
    </template>

    <AlertBox
      v-for="(warning, index) in warnings"
      :key="`${question.id}-warning-${index}`"
      class="lighting-advisor-question-field__warning"
      icon="attention"
      :style="'warning'"
      data-testid="lighting-advisor-spectrum-warning"
    >
      {{ warning.text }}
    </AlertBox>
  </div>
</template>

<script setup lang="ts">
import {
  AlertBox,
  Icon,
  InfoIcon,
  SelectableOptionItem,
  SelectableOptionItems
} from '@hypercodestudio/basler-components';
import type { MediaImageInterface } from '@hypercodestudio/basler-components';

import InputText from 'primevue/inputtext';
import type {
  LightingAdvisorQuestion,
  LightingAdvisorSetupField,
  LightingAnswers
} from '~/types/lightingAdvisor';
import { parseLightFunctionAnswers } from '~/utils/lightingAdvisor/parseAnswers';
import {
  DIMENSION_QUESTION_ID,
  DISTANCE_QUESTION_ID,
  type LightingAdvisorQuestionLayout,
  buildQuestionChoiceOptions,
  choiceGridColumns,
  isCheckboxQuestion,
  isInlineOptionQuestion,
  isListDisplayQuestion,
  isRadioQuestion,
  isTwoColumnLayout,
  isWideOptionListQuestion
} from '~/utils/lightingAdvisor/questionFieldKinds';
interface Props {
  question: LightingAdvisorQuestion;
  answers: LightingAnswers;
  layout: LightingAdvisorQuestionLayout;
  getTooltipKeys: (question: LightingAdvisorQuestion) => string[];
  getWarningNotes: (
    question: LightingAdvisorQuestion
  ) => Array<{ text: string }>;
  /** Follow-up question rendered inside this field (modal layout only). */
  nestedQuestion?: LightingAdvisorQuestion | null;
  optionImages?: Readonly<Partial<Record<string, MediaImageInterface>>>;
}

const props = withDefaults(defineProps<Props>(), {
  nestedQuestion: null,
  optionImages: () => ({})
});

const emit = defineEmits<{
  'update:answer': [questionId: string, value: string | null];
  'open-tooltip': [question: LightingAdvisorQuestion];
  /** Which measurement input has focus, so the setup sketch can highlight it. */
  'update:focused-field': [field: LightingAdvisorSetupField | null];
}>();

const { $textDictionary } = useNuxtApp();
const furtherApplicationInputId = useId();
const componentId = useId();

const questionLabelId = computed(
  () => `${componentId}-${props.question.id}-label`
);
const nestedQuestionLabelId = computed(
  () => `${componentId}-${props.nestedQuestion?.id ?? props.question.id}-label`
);
const usesColumns = computed(() => isTwoColumnLayout(props.layout));
const choiceDisplayType = computed<'grid' | 'list'>(() =>
  props.layout === 'plp-list' || isListDisplayQuestion(props.question.id)
    ? 'list'
    : 'grid'
);

const isRadio = computed(() => isRadioQuestion(props.question.id));
const isCheckbox = computed(() => isCheckboxQuestion(props.question.id));

const options = computed(() =>
  buildQuestionChoiceOptions(props.question, props.answers, props.optionImages)
);
const nestedOptions = computed(() =>
  props.nestedQuestion
    ? buildQuestionChoiceOptions(
        props.nestedQuestion,
        props.answers,
        props.optionImages
      )
    : []
);

const tooltipKeys = computed(() => props.getTooltipKeys(props.question));
const nestedTooltipKeys = computed(() =>
  props.nestedQuestion ? props.getTooltipKeys(props.nestedQuestion) : []
);
const warnings = computed(() => props.getWarningNotes(props.question));
const nestedWarnings = computed(() =>
  props.nestedQuestion ? props.getWarningNotes(props.nestedQuestion) : []
);

/**
 * Long option lists and the light function checkboxes span the full row, so
 * they get the three-column grid the legacy tool used.
 */
function optionGroupClassFor(questionId: string): string | null {
  if (isInlineOptionQuestion(questionId)) {
    return 'lighting-advisor-question-field__option-group--inline';
  }
  if (
    usesColumns.value &&
    (isCheckboxQuestion(questionId) || isWideOptionListQuestion(questionId))
  ) {
    return 'lighting-advisor-question-field__option-group--three-columns';
  }
  return null;
}

const optionGroupClass = computed(() => optionGroupClassFor(props.question.id));
const nestedOptionGroupClass = computed(() =>
  props.nestedQuestion ? optionGroupClassFor(props.nestedQuestion.id) : null
);

const infoButtonLabel = computed(
  () =>
    $textDictionary.value['lightingAdvisor.infoButton.label'] ??
    'Show information'
);

const furtherApplicationLabel = computed(
  () =>
    $textDictionary.value[
      'lightingAdvisor.question.a2s5q1.further_placeholder'
    ] ?? 'Describe your application'
);

function isFurtherApplicationOption(value: string): boolean {
  return props.question.id === 'a2s5q1' && value === 'further_application';
}

function isFunctionSelected(value: string): boolean {
  return parseLightFunctionAnswers(props.answers.a2s4q1).includes(value);
}

function toggleFunction(value: string) {
  const current = parseLightFunctionAnswers(props.answers.a2s4q1);
  const next = current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
  emitAnswer('a2s4q1', next.length ? next.join(',') : null);
}

function emitAnswer(questionId: string, value: string | null | undefined) {
  emit(
    'update:answer',
    questionId,
    value != null && value !== '' ? String(value) : null
  );
}
</script>

<style scoped>
.lighting-advisor-question-field {
  min-width: 0;
}

.lighting-advisor-question-field__label-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.lighting-advisor-question-field__label {
  margin: 0;
}

.lighting-advisor-question-field__info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.lighting-advisor-question-field__dimensions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.lighting-advisor-question-field__option-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.lighting-advisor-question-field__option-group--three-columns {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem 1.5rem;
}

.lighting-advisor-question-field__option-group--inline {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
}

.lighting-advisor-question-field__option-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.lighting-advisor-question-field__option-label {
  margin: 0;
  cursor: pointer;
}

.lighting-advisor-question-field__nested {
  margin-top: 1.25rem;
}

.lighting-advisor-question-field__further-input {
  position: relative;
  z-index: 1;
}

.lighting-advisor-question-field__warning {
  margin-top: 0.75rem;
}

/* The icon-only variant renders inline, so it needs a box for the margin. */
.lighting-advisor-question-field__warning-icon {
  display: block;
  margin-top: 0.75rem;
}

.lighting-advisor-question-field :deep(.p-inputtext) {
  width: 100%;
  max-width: 100%;
}

@media (max-width: 767px) {
  .lighting-advisor-question-field__dimensions,
  .lighting-advisor-question-field__option-group--three-columns {
    grid-template-columns: 1fr;
  }
}
</style>
